import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {number | undefined}
   */
  #deactivateTimer;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    window.clearTimeout(this.#deactivateTimer);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());
    window.clearTimeout(this.#deactivateTimer);

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const currentItem = this.#state.activeItem?.parentElement;
    const relatedTarget = event.relatedTarget instanceof Node ? event.relatedTarget : null;
    const isMovingWithinMenu =
      relatedTarget instanceof Node && Boolean(menu?.contains(relatedTarget) || currentItem?.contains(relatedTarget));
    const isMovingToSubmenu =
      relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(relatedTarget);
    const isMovingToOverflowMenu =
      relatedTarget instanceof Node && relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    window.clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = window.setTimeout(() => {
      const activeItem = this.#state.activeItem;
      const activeItemContainer = activeItem?.parentElement;
      const activeSubmenu = findSubmenu(activeItem);

      if (
        activeItemContainer?.matches(':hover') ||
        activeSubmenu?.matches(':hover') ||
        activeItemContainer?.contains(document.activeElement)
      ) {
        return;
      }

      this.#deactivate();
    }, 180);
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    window.clearTimeout(this.#deactivateTimer);

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

const FF_SHOP_MEGA_STYLE_ID = 'ff-shop-mega-js-styles';
const FF_SHOP_CARD_DATA = [
  {
    handle: 'sets',
    title: 'Anchor Sets',
    copy: 'Matching pullover and jogger kits.',
    url: '/collections/sets',
  },
  {
    handle: 'hoodies',
    title: 'Hoodies',
    copy: 'Heavyweight layers built for the long day.',
    url: '/collections/hoodies',
  },
  {
    handle: 'headwear',
    title: 'Headwear',
    copy: 'Clean finishing pieces for every kit.',
    url: '/collections/headwear',
  },
];

function initFatherFortifiedShopMega() {
  if (!window.matchMedia('(min-width: 990px)').matches) return;

  injectFatherFortifiedShopMegaStyles();

  document.querySelectorAll('header-menu').forEach((headerMenu) => {
    if (!(headerMenu instanceof HTMLElement)) return;

    const shopItem = findShopMenuItem(headerMenu);
    if (!shopItem || shopItem.dataset.ffShopMegaReady === 'true') return;

    const trigger = shopItem.querySelector('.menu-list__link');
    const submenu = shopItem.querySelector('.menu-list__submenu');
    if (!(trigger instanceof HTMLElement) || !(submenu instanceof HTMLElement)) return;

    const existingCustomMenu = submenu.querySelector('.ff-shop-mega, .ff-shop-mega-js');

    if (!existingCustomMenu) {
      const target = submenu.querySelector('.mega-menu__grid') || submenu.querySelector('.menu-list__submenu-inner') || submenu;
      if (!(target instanceof HTMLElement)) return;

      const categoryLinks = getShopMegaLinks(submenu);
      target.innerHTML = renderFatherFortifiedShopMega(categoryLinks);
      hydrateFatherFortifiedShopCards(target);
    }

    shopItem.classList.add('ff-shop-mega-js-item');
    bindFatherFortifiedShopMega(shopItem, trigger, submenu);
    shopItem.dataset.ffShopMegaReady = 'true';
  });
}

function findShopMenuItem(headerMenu) {
  const items = Array.from(headerMenu.querySelectorAll('.menu-list__list-item'));

  return items.find((item) => {
    if (!(item instanceof HTMLElement)) return false;

    const title = item.querySelector('.menu-list__link-title')?.textContent?.trim().toLowerCase();
    return title === 'shop' && item.querySelector('.menu-list__submenu');
  });
}

function getShopMegaLinks(submenu) {
  const links = Array.from(submenu.querySelectorAll('.mega-menu__link[href], .ff-shop-mega__category-link[href]'))
    .map((link) => ({
      title: normalizeShopMenuTitle(link.textContent || ''),
      url: link.getAttribute('href') || '',
    }))
    .filter((link) => link.title && link.url && link.title.toLowerCase() !== 'digital gift card');

  const fallbackLinks = [
    { title: 'Shop All', url: '/collections/all' },
    { title: 'Hoodies', url: '/collections/hoodies' },
    { title: 'Joggers', url: '/collections/joggers' },
    { title: 'Shorts', url: '/collections/shorts' },
    { title: 'Tops', url: '/collections/tops' },
    { title: 'Tanks', url: '/collections/tanks' },
    { title: 'Headwear', url: '/collections/headwear' },
  ];

  return uniqueShopLinks([
    { title: 'Sets', url: '/collections/sets' },
    ...(links.length > 0 ? links : fallbackLinks),
  ]);
}

function normalizeShopMenuTitle(title) {
  return title
    .replace(/\s+/g, ' ')
    .replace(/Tees\s*&\s*Tops/gi, 'Tops')
    .replace(/\bView\b/gi, '')
    .trim();
}

function uniqueShopLinks(links) {
  const seen = new Set();

  return links.filter((link) => {
    const key = `${link.title.toLowerCase()}|${link.url.split('?')[0]}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function renderFatherFortifiedShopMega(categoryLinks) {
  const linksMarkup = categoryLinks.map((link) => `
    <li>
      <a href="${escapeHtml(link.url)}" class="ff-shop-mega-js__category-link">
        <span>${escapeHtml(link.title)}</span>
        <span class="ff-shop-mega-js__category-action">View</span>
      </a>
    </li>
  `).join('');

  const cardsMarkup = FF_SHOP_CARD_DATA.map((card) => `
    <a href="${escapeHtml(card.url)}" class="ff-shop-mega-js__card" data-ff-shop-card="${escapeHtml(card.handle)}">
      <span class="ff-shop-mega-js__card-media">
        <img alt="${escapeHtml(card.title)}" width="620" height="420" loading="lazy">
      </span>
      <span class="ff-shop-mega-js__card-body">
        <span class="ff-shop-mega-js__card-title">${escapeHtml(card.title)}</span>
        <span class="ff-shop-mega-js__card-copy">${escapeHtml(card.copy)}</span>
      </span>
    </a>
  `).join('');

  return `
    <div class="ff-shop-mega-js">
      <div class="ff-shop-mega-js__panel">
        <div class="ff-shop-mega-js__lead">
          <p class="ff-shop-mega-js__eyebrow">Shop</p>
          <a href="/collections/all" class="ff-shop-mega-js__title">The Foundation Collection</a>
          <p class="ff-shop-mega-js__copy">
            Built-for-fathers essentials, complete sets, and launch-ready staples in one place.
          </p>
          <div class="ff-shop-mega-js__actions">
            <a href="/collections/all" class="ff-shop-mega-js__button">Shop All</a>
            <a href="/collections/sets" class="ff-shop-mega-js__text-link">Anchor Sets</a>
          </div>
        </div>

        <div class="ff-shop-mega-js__links">
          <p class="ff-shop-mega-js__section-title">Categories</p>
          <ul class="ff-shop-mega-js__link-list list-unstyled">${linksMarkup}</ul>
          <a href="/products/digital-gift-card" class="ff-shop-mega-js__gift-link">Digital Gift Card</a>
        </div>

        <div class="ff-shop-mega-js__cards">${cardsMarkup}</div>
      </div>
    </div>
  `;
}

function bindFatherFortifiedShopMega(shopItem, trigger, submenu) {
  if (shopItem.dataset.ffShopMegaBound === 'true') return;

  let closeTimer;
  const headerComponent = shopItem.closest('header-component');

  const suppressHeaderUnderlay = () => {
    if (!(headerComponent instanceof HTMLElement)) return;

    headerComponent.classList.add('ff-shop-mega-is-open');
    headerComponent.style.setProperty('--submenu-height', '0px');
    headerComponent.style.setProperty('--full-open-header-height', '0px');
  };

  const open = (event) => {
    event?.stopImmediatePropagation?.();
    window.clearTimeout(closeTimer);
    trigger.setAttribute('aria-expanded', 'true');
    submenu.dataset.active = '';
    requestAnimationFrame(suppressHeaderUnderlay);
  };

  const close = (event) => {
    event?.stopImmediatePropagation?.();
    window.clearTimeout(closeTimer);
    closeTimer = window.setTimeout(() => {
      if (shopItem.matches(':hover') || submenu.matches(':hover') || shopItem.contains(document.activeElement)) return;

      trigger.setAttribute('aria-expanded', 'false');
      delete submenu.dataset.active;

      if (headerComponent instanceof HTMLElement) {
        headerComponent.classList.remove('ff-shop-mega-is-open');
        headerComponent.style.setProperty('--submenu-height', '0px');
        headerComponent.style.setProperty('--full-open-header-height', '0px');
      }
    }, 250);
  };

  shopItem.addEventListener('pointerenter', open, true);
  shopItem.addEventListener('pointerleave', close, true);
  submenu.addEventListener('pointerenter', open, true);
  submenu.addEventListener('pointerleave', close, true);
  shopItem.addEventListener('focusin', open, true);
  shopItem.addEventListener('focusout', close, true);
  shopItem.addEventListener('keydown', (event) => {
    if (!(event instanceof KeyboardEvent)) return;

    if (event.key === 'Escape') {
      event.stopImmediatePropagation();
      trigger.setAttribute('aria-expanded', 'false');
      delete submenu.dataset.active;
      headerComponent?.classList.remove('ff-shop-mega-is-open');
      trigger.focus();
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopImmediatePropagation();
      open(event);
      submenu.querySelector('a[href]')?.focus();
    }
  }, true);

  shopItem.dataset.ffShopMegaBound = 'true';
}

function hydrateFatherFortifiedShopCards(container) {
  container.querySelectorAll('[data-ff-shop-card]').forEach(async (card) => {
    if (!(card instanceof HTMLElement)) return;

    const handle = card.dataset.ffShopCard;
    const img = card.querySelector('img');
    if (!handle || !(img instanceof HTMLImageElement)) return;

    const imageUrl = await getCollectionProductImage(handle);
    if (!imageUrl) {
      img.remove();
      return;
    }

    img.src = imageUrl;
  });
}

async function getCollectionProductImage(handle) {
  const cacheKey = `ff-shop-card-image:${handle}`;
  let cachedValue = '';

  try {
    cachedValue = sessionStorage.getItem(cacheKey) || '';
  } catch {
    cachedValue = '';
  }

  if (cachedValue) return cachedValue;

  try {
    const response = await fetch(`/collections/${handle}/products.json?limit=1`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) return '';

    const data = await response.json();
    const product = data?.products?.[0];
    const imageUrl = product?.images?.[0]?.src || product?.image?.src || '';

    if (imageUrl) {
      try {
        sessionStorage.setItem(cacheKey, imageUrl);
      } catch {
        // Card images are an enhancement; the menu remains usable without storage.
      }
    }

    return imageUrl;
  } catch {
    return '';
  }
}

function injectFatherFortifiedShopMegaStyles() {
  if (document.getElementById(FF_SHOP_MEGA_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = FF_SHOP_MEGA_STYLE_ID;
  style.textContent = `
    .header-menu .ff-shop-mega-js-item::after {
      height: max(12px, var(--header-padding, 12px));
      margin-bottom: calc(-1 * max(12px, var(--header-padding, 12px)));
    }

    .header-menu .ff-shop-mega-js-item > .menu-list__submenu {
      top: calc(100% - 1px);
      clip-path: none;
      padding: 0 clamp(16px, 3vw, 48px);
    }

    /* Hover bridge: full-width strip over the header's bottom padding so a diagonal
       move from the Shop link down to the panel never leaves the hover area. */
    .header-menu .ff-shop-mega-js-item > .menu-list__submenu::before {
      content: '';
      position: absolute;
      inset-inline: 0;
      top: calc(-1 * max(12px, var(--header-padding, 12px)) - 2px);
      height: calc(max(12px, var(--header-padding, 12px)) + 2px);
    }

    .header-menu .ff-shop-mega-js-item > .menu-list__submenu .menu-list__submenu-inner {
      max-height: calc(100vh - 120px);
      padding: 0;
      overflow: visible;
      transform: none;
    }

    .header-menu .ff-shop-mega-js-item .mega-menu__grid {
      display: block;
    }

    #header-component.ff-shop-mega-is-open .header__underlay-open,
    #header-component:has(.ff-shop-mega-js-item > .menu-list__link[aria-expanded='true']) .header__underlay-open,
    #header-component:has(.ff-shop-menu-item:where(:hover, :focus-within)) .header__underlay-open,
    #header-component:has(.ff-shop-menu-item[data-ff-shop-menu-open='true']) .header__underlay-open {
      height: var(--header-height);
    }

    #header-component.ff-shop-mega-is-open .header__underlay-open::after,
    #header-component:has(.ff-shop-mega-js-item > .menu-list__link[aria-expanded='true']) .header__underlay-open::after,
    #header-component:has(.ff-shop-menu-item:where(:hover, :focus-within)) .header__underlay-open::after,
    #header-component:has(.ff-shop-menu-item[data-ff-shop-menu-open='true']) .header__underlay-open::after {
      box-shadow: none;
    }

    .ff-shop-mega-js,
    .ff-shop-mega,
    .ff-shop-mega-runtime {
      max-width: min(1320px, calc(100vw - 72px));
      margin: 0 auto;
      white-space: normal;
    }

    .ff-shop-mega-js__panel,
    .ff-shop-mega__panel,
    .ff-shop-mega-runtime__panel {
      display: grid;
      grid-template-columns: minmax(260px, 0.85fr) minmax(310px, 1fr) minmax(460px, 1.25fr);
      gap: 24px;
      padding: 28px;
      background: #1c1c1c;
      border: 1px solid rgb(255 255 255 / 10%);
      box-shadow: 0 22px 50px rgb(0 0 0 / 28%);
      color: #f7f3ed;
      white-space: normal;
    }

    .ff-shop-mega-js__eyebrow,
    .ff-shop-mega-js__section-title,
    .ff-shop-mega__eyebrow,
    .ff-shop-mega__section-title,
    .ff-shop-mega-runtime__eyebrow,
    .ff-shop-mega-runtime__section-title {
      margin: 0 0 14px;
      color: #A8A8A3;
      font-family: var(--font-subheading--family);
      font-size: 0.72rem;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    .ff-shop-mega-js__title,
    .ff-shop-mega__title,
    .ff-shop-mega-runtime__title {
      display: block;
      color: #fff;
      font-family: var(--font-heading--family);
      font-size: clamp(1.28rem, 1.08rem + 0.5vw, 1.68rem);
      line-height: 1.08;
      max-width: 13ch;
      text-decoration: none;
      white-space: normal;
    }

    .ff-shop-mega-js__copy,
    .ff-shop-mega__copy,
    .ff-shop-mega-runtime__copy {
      max-width: 31ch;
      margin: 12px 0 20px;
      color: rgb(247 243 237 / 72%);
      font-size: 0.92rem;
      line-height: 1.5;
      white-space: normal;
    }

    .ff-shop-mega-js__actions,
    .ff-shop-mega__actions,
    .ff-shop-mega-runtime__actions {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 14px;
    }

    .ff-shop-mega-js__button,
    .ff-shop-mega-js__text-link,
    .ff-shop-mega-js__gift-link,
    .ff-shop-mega__button,
    .ff-shop-mega__text-link,
    .ff-shop-mega__gift-link,
    .ff-shop-mega-runtime__button,
    .ff-shop-mega-runtime__text-link,
    .ff-shop-mega-runtime__gift-link {
      font-family: var(--font-subheading--family);
      font-size: 0.78rem;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      text-decoration: none;
    }

    .ff-shop-mega-js__button,
    .ff-shop-mega__button,
    .ff-shop-mega-runtime__button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-height: 40px;
      padding: 0 18px;
      background: #ffffff;
      color: #0d0d0d;
      border-radius: 999px;
    }

    .ff-shop-mega-js__button:hover,
    .ff-shop-mega__button:hover,
    .ff-shop-mega-runtime__button:hover {
      background: #e9e9e9;
      color: #0d0d0d;
    }

    .ff-shop-mega-js__text-link,
    .ff-shop-mega-js__gift-link,
    .ff-shop-mega-js__category-link,
    .ff-shop-mega-js__card,
    .ff-shop-mega__text-link,
    .ff-shop-mega__gift-link,
    .ff-shop-mega__category-link,
    .ff-shop-mega__card,
    .ff-shop-mega-runtime__text-link,
    .ff-shop-mega-runtime__gift-link,
    .ff-shop-mega-runtime__category-link,
    .ff-shop-mega-runtime__card {
      color: #f7f3ed;
    }

    .ff-shop-mega-js__title:hover,
    .ff-shop-mega-js__text-link:hover,
    .ff-shop-mega-js__gift-link:hover,
    .ff-shop-mega-js__category-link:hover,
    .ff-shop-mega-js__card:hover,
    .ff-shop-mega__title:hover,
    .ff-shop-mega__text-link:hover,
    .ff-shop-mega__gift-link:hover,
    .ff-shop-mega__category-link:hover,
    .ff-shop-mega__card:hover,
    .ff-shop-mega-runtime__title:hover,
    .ff-shop-mega-runtime__text-link:hover,
    .ff-shop-mega-runtime__gift-link:hover,
    .ff-shop-mega-runtime__category-link:hover,
    .ff-shop-mega-runtime__card:hover {
      color: #A8A8A3;
    }

    .ff-shop-mega-js__link-list,
    .ff-shop-mega__link-list,
    .ff-shop-mega-runtime__link-list {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 2px 18px;
    }

    .ff-shop-mega-js__category-link,
    .ff-shop-mega__category-link,
    .ff-shop-mega-runtime__category-link {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 14px;
      min-height: 36px;
      border-bottom: 1px solid rgb(255 255 255 / 9%);
      font-size: 0.9rem;
      text-decoration: none;
      white-space: normal;
    }

    .ff-shop-mega-js__category-action,
    .ff-shop-mega__arrow,
    .ff-shop-mega-runtime__arrow {
      color: rgb(247 243 237 / 46%);
      font-size: 0.72rem;
      flex-shrink: 0;
      text-transform: uppercase;
    }

    .ff-shop-mega-js__gift-link,
    .ff-shop-mega__gift-link,
    .ff-shop-mega-runtime__gift-link {
      display: inline-flex;
      margin-top: 18px;
    }

    .ff-shop-mega-js__cards,
    .ff-shop-mega__cards,
    .ff-shop-mega-runtime__cards {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 14px;
    }

    .ff-shop-mega-js__card,
    .ff-shop-mega__card,
    .ff-shop-mega-runtime__card {
      display: flex;
      min-height: 100%;
      flex-direction: column;
      overflow: hidden;
      background: #242424;
      text-decoration: none;
      white-space: normal;
    }

    .ff-shop-mega-js__card-media,
    .ff-shop-mega__card-media,
    .ff-shop-mega-runtime__card-media {
      display: block;
      aspect-ratio: 4 / 3;
      background: linear-gradient(135deg, #2b3b4a, #1c1c1c);
      overflow: hidden;
    }

    .ff-shop-mega-js__card-media img,
    .ff-shop-mega__card-media img,
    .ff-shop-mega-runtime__card-media img {
      width: 100%;
      height: 100%;
      display: block;
      object-fit: cover;
      transition: transform 220ms var(--ease-out-cubic);
    }

    .ff-shop-mega-js__card:hover img,
    .ff-shop-mega__card:hover img,
    .ff-shop-mega-runtime__card:hover img {
      transform: scale(1.035);
    }

    .ff-shop-mega-js__card-body,
    .ff-shop-mega__card-body,
    .ff-shop-mega-runtime__card-body {
      display: grid;
      gap: 6px;
      padding: 14px;
    }

    .ff-shop-mega-js__card-title,
    .ff-shop-mega__card-title,
    .ff-shop-mega-runtime__card-title {
      font-family: var(--font-subheading--family);
      font-size: 0.82rem;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    .ff-shop-mega-js__card-copy,
    .ff-shop-mega__card-copy,
    .ff-shop-mega-runtime__card-copy {
      color: rgb(247 243 237 / 62%);
      font-size: 0.78rem;
      line-height: 1.35;
      white-space: normal;
    }

    @media screen and (max-width: 1240px) {
      .ff-shop-mega-js__panel,
      .ff-shop-mega__panel,
      .ff-shop-mega-runtime__panel {
        grid-template-columns: minmax(240px, 0.8fr) minmax(300px, 1fr);
        gap: 22px;
        padding: 24px;
      }

      .ff-shop-mega-js__cards,
      .ff-shop-mega__cards,
      .ff-shop-mega-runtime__cards {
        grid-column: 1 / -1;
      }
    }

    @media screen and (max-width: 1080px) {
      .ff-shop-mega-js,
      .ff-shop-mega,
      .ff-shop-mega-runtime {
        max-width: calc(100vw - 32px);
      }

      .ff-shop-mega-js__panel,
      .ff-shop-mega__panel,
      .ff-shop-mega-runtime__panel {
        grid-template-columns: minmax(220px, 0.8fr) minmax(260px, 1fr);
      }
    }
  `;

  document.head.append(style);
}

function escapeHtml(value) {
  return String(value || '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[char]);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFatherFortifiedShopMega, { once: true });
} else {
  initFatherFortifiedShopMega();
}

requestAnimationFrame(initFatherFortifiedShopMega);
setTimeout(initFatherFortifiedShopMega, 500);
window.addEventListener('resize', debounce(initFatherFortifiedShopMega, 150));
document.addEventListener('shopify:section:load', initFatherFortifiedShopMega);

function initFatherFortifiedFeaturedCarouselFix() {
  document.querySelectorAll('.ff-featured-collection-section .ffc[data-layout="carousel"]').forEach((sectionEl) => {
    if (!(sectionEl instanceof HTMLElement) || sectionEl.dataset.ffCarouselFixed === 'true') return;

    const track = sectionEl.querySelector('[data-carousel]');
    const prevBtn = sectionEl.querySelector('[data-prev]');
    const nextBtn = sectionEl.querySelector('[data-next]');
    const dotsContainer = sectionEl.querySelector('[data-dots]');

    if (!(track instanceof HTMLElement) || !(prevBtn instanceof HTMLButtonElement) || !(nextBtn instanceof HTMLButtonElement)) {
      return;
    }

    sectionEl.dataset.ffCarouselFixed = 'true';

    const getOrderedCards = () => Array.from(track.querySelectorAll('.ffc__card'))
      .filter((card) => card instanceof HTMLElement)
      .sort((a, b) => {
        const offsetDiff = a.offsetLeft - b.offsetLeft;
        if (Math.abs(offsetDiff) > 1) return offsetDiff;

        const aOrder = Number.parseInt(window.getComputedStyle(a).order || '0', 10);
        const bOrder = Number.parseInt(window.getComputedStyle(b).order || '0', 10);
        return aOrder - bOrder;
      });

    const getGap = (cards) => {
      if (cards.length < 2) return 0;
      return Math.max(0, cards[1].offsetLeft - cards[0].offsetLeft - cards[0].offsetWidth);
    };

    const getVisibleCount = () => {
      const cards = getOrderedCards();
      if (!cards.length) return 1;

      const cardWidth = cards[0].offsetWidth;
      const gap = getGap(cards);
      const step = cardWidth + gap;

      if (step <= 0) return 1;
      return Math.max(1, Math.round(track.clientWidth / step));
    };

    const getPageOffsets = () => {
      const cards = getOrderedCards();
      const perPage = getVisibleCount();
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const offsets = [];

      for (let index = 0; index < cards.length; index += perPage) {
        offsets.push(Math.min(cards[index].offsetLeft, maxScroll));
      }

      return offsets.filter((offset, index) => index === 0 || Math.abs(offset - offsets[index - 1]) > 2);
    };

    const getCurrentPage = () => {
      const offsets = getPageOffsets();
      let page = 0;
      let closestDistance = Infinity;

      offsets.forEach((offset, index) => {
        const distance = Math.abs(track.scrollLeft - offset);
        if (distance < closestDistance) {
          closestDistance = distance;
          page = index;
        }
      });

      return page;
    };

    const updateNav = () => {
      const offsets = getPageOffsets();
      const page = getCurrentPage();

      prevBtn.disabled = page <= 0;
      nextBtn.disabled = page >= offsets.length - 1;

      if (dotsContainer) {
        dotsContainer.querySelectorAll('.ffc__dot').forEach((dot, index) => {
          dot.classList.toggle('is-active', index === page);
        });
      }
    };

    const scrollToPage = (page) => {
      const offsets = getPageOffsets();
      if (!offsets.length) return;

      const targetPage = Math.max(0, Math.min(page, offsets.length - 1));
      track.scrollTo({ left: offsets[targetPage], behavior: 'smooth' });
      requestAnimationFrame(updateNav);
      setTimeout(updateNav, 450);
    };

    const rebuildDots = () => {
      if (!dotsContainer) return;

      const pages = getPageOffsets().length;
      dotsContainer.innerHTML = '';

      if (pages <= 1) return;

      for (let index = 0; index < pages; index += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = `ffc__dot${index === getCurrentPage() ? ' is-active' : ''}`;
        dot.dataset.page = String(index);
        dot.setAttribute('aria-label', `Page ${index + 1}`);
        dotsContainer.append(dot);
      }
    };

    prevBtn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      scrollToPage(getCurrentPage() - 1);
    }, true);

    nextBtn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      scrollToPage(getCurrentPage() + 1);
    }, true);

    dotsContainer?.addEventListener('click', (event) => {
      const dot = event.target instanceof Element ? event.target.closest('.ffc__dot') : null;
      if (!(dot instanceof HTMLButtonElement) || !dot.dataset.page) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      scrollToPage(Number.parseInt(dot.dataset.page, 10));
    }, true);

    track.addEventListener('scroll', debounce(updateNav, 80), { passive: true });
    window.addEventListener('resize', debounce(() => {
      rebuildDots();
      updateNav();
    }, 150));

    rebuildDots();
    updateNav();
    setTimeout(() => {
      rebuildDots();
      updateNav();
    }, 600);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFatherFortifiedFeaturedCarouselFix, { once: true });
} else {
  initFatherFortifiedFeaturedCarouselFix();
}

requestAnimationFrame(initFatherFortifiedFeaturedCarouselFix);
setTimeout(initFatherFortifiedFeaturedCarouselFix, 700);
document.addEventListener('shopify:section:load', initFatherFortifiedFeaturedCarouselFix);
